import React from "react";
import { useTranslation } from "react-i18next";
import { Close } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { formatIsoDate } from "@/utils/datetime";
import { Button } from "@/components/ui/button";
import DreamerIcons from "./DreamerIcons";
import PreviousVersion from "./PreviousVersion";
import { IconButton } from "@/components/ui/icon-button";
import PopoverWrapper from "../PopoverWrapper";
import { BaseIndicationProps, IndicationData } from "../types";
import { usePopoverState } from "../hooks";

const TRANSLATION_KEY = "deals.dealView.filledByDreamer";

interface Props extends BaseIndicationProps {
  indicationData: IndicationData;
}

const ModifiedManuallyIndication: React.FC<Props> = ({
  dealId,
  questionnaireId,
  questionId,
  questionResponse,
  restorePreviousVersionButton = true,
  showSources = true,
  size,
  indicationData
}) => {
  const { t } = useTranslation();
  const {
    isPopoverOpen,
    setIsPopoverOpen,
    showPreviousVersion,
    setShowPreviousVersion
  } = usePopoverState();

  const { lastEditedByData } = indicationData;

  const displayDate = formatIsoDate(questionResponse?.createdAt);

  const indicationText = t(`${TRANSLATION_KEY}.modifiedByUser`, {
    userName: lastEditedByData?.displayName
  });

  const previousLastEditedBy = questionResponse?.revisions?.[0]
    ? questionResponse?.revisions?.[0]?.lastEditedBy
    : null;

  const triggerContent = (
    <DreamerIcons
      dreamerMode={false}
      isLastEditedByDreamer={false}
      lastEditedByData={lastEditedByData}
      previousLastEditedBy={previousLastEditedBy}
      size={size}
    />
  );

  const popoverContent = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center">
            <DreamerIcons
              dreamerMode={false}
              isLastEditedByDreamer={false}
              lastEditedByData={lastEditedByData}
              previousLastEditedBy={previousLastEditedBy}
              size="md"
            />
          </div>
          <Text variant="lgLabel">{indicationText}</Text>
        </div>
        <div className="flex items-center gap-4">
          {displayDate ? (
            <Text variant="bodyTertiary">{displayDate}</Text>
          ) : null}
          <IconButton
            aria-label={t("close")}
            onClick={() => setIsPopoverOpen(false)}
            variant="iconNoBg"
            iconColor="secondary"
            size="md"
          >
            <Close />
          </IconButton>
        </div>
      </div>
      {showPreviousVersion && questionResponse?.revisions ? (
        <PreviousVersion
          dealId={dealId}
          questionnaireId={questionnaireId}
          questionId={questionId}
          revision={questionResponse?.revisions[0]}
          setIsPopoverOpen={setIsPopoverOpen}
          showRestoreButton={restorePreviousVersionButton}
          showSources={showSources}
        />
      ) : (
        <Button variant="tertiary" onClick={() => setShowPreviousVersion(true)}>
          {t(`${TRANSLATION_KEY}.previousVersionButton`)}
        </Button>
      )}
    </>
  );

  return (
    <PopoverWrapper
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
      triggerContent={triggerContent}
      tooltipText={indicationText}
      popoverContent={popoverContent}
    />
  );
};

export default ModifiedManuallyIndication;
